/**
 * The a*b* chromaticity scatter shared by the exploration and selection pages.
 *
 * Points are drawn at their own measured colour on a fixed -60..60 square, so
 * two plots of different data stay visually comparable - the axes never
 * rescale to the data. Every sample is drawn: thinning the cloud made two
 * plots of nested sets (full pool vs. pruned) show different subsets, so a
 * point could appear only in the smaller one.
 *
 * The download button rasterises the chart through html2canvas, which is why
 * the chart keeps its own ref.
 */
import React, { useMemo, useRef } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import { IconButton, Box, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import html2canvas from 'html2canvas';

const defaultMargin = { top: 12, right: 16, left: 12, bottom: 28 };

/** The symmetric axis limit covering every point of ``points`` (a*, b*):
 *  at least 60, rounded up to a multiple of 10. Shared by both axes so the
 *  gamut keeps its shape. */
export function axisLimit(points) {
  const values = (Array.isArray(points) ? points : [])
    .flatMap((entry) => [Math.abs(Number(entry?.a_value)), Math.abs(Number(entry?.b_value))])
    .filter(Number.isFinite);
  const largest = values.length ? Math.max(...values) : 0;
  return Math.max(60, Math.ceil(largest / 10) * 10);
}

const MyScatterChart = ({
  dataColor,
  // The points the axes must cover (the whole exploration); the axes then
  // stay put while a slider or scope switch changes ``dataColor``.
  domainData,
  width = 520,
  height = 380,
  margin = defaultMargin,
}) => {
  const chartRef = useRef(null);

  const limit = axisLimit(domainData || dataColor);
  const xDomain = [-limit, limit];
  const yDomain = [-limit, limit];
  const xTicks = [-limit, -limit / 2, 0, limit / 2, limit];
  const yTicks = [-limit, -limit / 2, 0, limit / 2, limit];

  const formatAxisTick = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "";
    return String(Math.round(numeric));
  };

  const displayData = useMemo(
    () => (Array.isArray(dataColor) ? dataColor : []),
    [dataColor],
  );
  const handleDownload = async () => {
    if (chartRef.current) {
      try {
        const canvas = await html2canvas(chartRef.current);
        const image = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = image;
        link.download = 'scatter-plot.png';
        link.click();
      } catch (error) {
        console.error('Error downloading chart:', error);
      }
    }
  };

  if (!dataColor || dataColor.length === 0) {
    return (
      <Box
        sx={{
          width,
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 2,
          bgcolor: "action.hover",
          color: "text.secondary",
          typography: "body2",
        }}
      >
        No color samples to display
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width, flexShrink: 0, mx: 'auto' }}>
      <div ref={chartRef}>
        <ScatterChart width={width} height={height} margin={margin}>
          <XAxis
            type="number"
            domain={xDomain}
            ticks={xTicks}
            allowDataOverflow
            allowDecimals={false}
            tickFormatter={formatAxisTick}
            dataKey="a_value"
            name="CIE-a*"
            tick={{ fontSize: 11 }}
            stroke="#94a3b8"
            label={{ value: "CIE-a*", angle: 0, position: "insideBottom", offset: -12, fill: "#64748b", fontSize: 12 }}
          />
          <YAxis
            type="number"
            domain={yDomain}
            ticks={yTicks}
            allowDataOverflow
            allowDecimals={false}
            tickFormatter={formatAxisTick}
            dataKey="b_value"
            name="CIE-b*"
            tick={{ fontSize: 11 }}
            stroke="#94a3b8"
            label={{ value: "CIE-b*", angle: -90, position: "center", fill: "#64748b", fontSize: 12 }}
          />
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <Tooltip
            contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
            cursor={{ stroke: "#cbd5e1", strokeDasharray: "3 3" }}
          />
          <Scatter data={displayData} isAnimationActive={false}>
            {displayData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={`rgb(${entry.color.join(',')})`} />
            ))}
          </Scatter>
        </ScatterChart>
      </div>
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          left: 8,
          bottom: 6,
          color: "text.secondary",
          bgcolor: "rgba(255,255,255,0.9)",
          px: 0.75,
          borderRadius: 0.75,
        }}
      >
        {displayData.length.toLocaleString()} points
      </Typography>
      <IconButton
        onClick={handleDownload}
        size="small"
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          bgcolor: "rgba(255, 255, 255, 0.92)",
          boxShadow: 1,
          "&:hover": { bgcolor: "background.paper" },
        }}
        aria-label="Download chart as PNG"
      >
        <DownloadIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default MyScatterChart;